/**
 * The CUSTOMER's own orcaloops-archive RPC definitions (OL-152).
 *
 * The customer door onto the same archived runs the operator door serves, with
 * three deliberate subtractions:
 *
 * **No payload carries a `customerId`.** Both methods take an `identifier` and
 * nothing else; the owning customer is resolved server-side from
 * `CurrentActor`. A door that accepted a subject id would be one missing check
 * away from letting any signed-in person read another customer's build
 * transcripts — so the field does not exist on the wire and the mistake is
 * unrepresentable rather than merely unchecked.
 *
 * **No `downloadUrl`.** Raw archived bytes are operator-only; a customer sees
 * the redacted text projection or nothing. The method lives solely on
 * `rpc/admin/orca-archive.rpc.ts`.
 *
 * **No `RecordNotFoundError` — this door is not an existence oracle.** An
 * identifier that does not exist and an identifier belonging to somebody else
 * must fail IDENTICALLY, as `Forbidden`; a distinguishable "not found" would
 * let a caller enumerate which identifiers are real. The union below is frozen
 * for exactly that reason: the handler seam may not edit this file, so it
 * cannot widen the union and re-introduce the oracle later.
 *
 * {@link ArchiveNotReady} is a PERMANENT member of both unions — it is also the
 * honest error when the reader has no AWS configuration — so replacing a stub
 * handler body is not a wire change.
 *
 * Every schema comes from `../orca-archive.schemas.ts`; this file declares none
 * of its own.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AuthMiddleware } from "../auth.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";
import {
  ArchiveListInput,
  ArchiveNotReady,
  ArchiveObjectNotRenderable,
  ArchiveObjectTooLarge,
  ArchiveTextInput,
  OrcaArchiveListing,
  OrcaArchiveText,
} from "../orca-archive.schemas.ts";

// ============================================================================
// RPC Group
// ============================================================================

/**
 * The customer's own orcaloops-archive RPC group. Every method carries
 * `.middleware(AuthMiddleware)` per `Rpc.make`, never on the group.
 *
 * @since 1.0.0
 */
export class OrcaloopsArchiveRpcs extends RpcGroup.make(
  Rpc.make("orcaloops.archive.list", {
    payload: ArchiveListInput,
    success: OrcaArchiveListing,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, ArchiveNotReady),
  }).middleware(AuthMiddleware),
  Rpc.make("orcaloops.archive.text", {
    payload: ArchiveTextInput,
    success: OrcaArchiveText,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      ArchiveNotReady,
      ArchiveObjectTooLarge,
      ArchiveObjectNotRenderable,
    ),
  }).middleware(AuthMiddleware),
) {}
